import { STATES, PROVINCES } from "../constants/states";

// Roman numerals to keep capitalized
const ROMAN_NUMERALS = new Set([
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
]);

// State and province abbreviations to keep capitalized
const STATE_ABBREVIATIONS = new Set<string>([
  ...(STATES as string[]),
  ...(PROVINCES as string[]),
]);

const REPORTING_RELATED_ABBREVIATIONS = new Set<string>([
  "VSQG",
  "SQG",
  "LQG",
  "MPCA",
  "HW",
]);

const OTHER_MAPPINGS = new Map<string, string>([
  ["RCRAINFO", "RCRAInfo"],
  ["EVQ", "eVQ"],
]);

/**
 * Pretty prints an enum value by capitalizing the first letter of each word
 * and keeping certain abbreviations capitalized.
 * @param enumValue - The enum value to pretty print
 * @param additionalAbbreviations - Additional abbreviations to keep capitalize
 * @param ignoreStateAbbreviations - Whether to ignore state abbreviations
 * @returns The pretty printed enum value
 */
export function prettyPrintEnumValue(
  enumValue: any,
  additionalAbbreviations?: string[],
  ignoreStateAbbreviations?: boolean
): string {
  if (!enumValue) {
    return "";
  }
  const words: string[] = enumValue
    ?.toString()
    // Replace underscores with spaces
    .replace(/_/g, " ")
    // Replace a camelCase or PascalCase boundary with a space (e.g. sendEmail -> send email)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ");

  // ENUM_LIKE_THIS
  const isEntireEnumUpperCase = words.every(
    (word) => word === word.toUpperCase()
  );

  const residualWords = words.map((word: string) => {
    if (word === word.toUpperCase()) {
      if (!isEntireEnumUpperCase) {
        // this must be a stray abbreviation
        return word;
      }
      // Keep Roman numerals capitalized
      if (ROMAN_NUMERALS.has(word)) {
        return word;
      }
      // Keep state abbreviations capitalized
      if (STATE_ABBREVIATIONS.has(word) && !ignoreStateAbbreviations) {
        return word;
      }
      // Keep reporting related abbreviations capitalized
      if (REPORTING_RELATED_ABBREVIATIONS.has(word)) {
        return word;
      }

      if (OTHER_MAPPINGS.has(word)) {
        return OTHER_MAPPINGS.get(word) || word;
      }

      if (additionalAbbreviations?.includes(word)) {
        return word;
      }

      return capitalize(word);
    }
    // if the word contains numbers, capitalize the letter after each number, and the first letter
    if (word.match(/\d+/)) {
      return handleWordWithNumbers(word);
    }
    // If the word has multiple uppercase letters to start then lowercase the whole word except for the first letter
    if (word.match(/^[A-Z][A-Z]+[a-z]/)) {
      return handleMixedCaseWord(word);
    }
    return capitalize(word);
  });
  return residualWords.join(" ");
}

function capitalize(word: string) {
  return word.slice(0, 1).toUpperCase() + word.slice(1).toLowerCase();
}

// A word like Tier2Submit, or Tier2Manager
// this one is a little more opinionated but it works in our context
function handleWordWithNumbers(word: string) {
  // Capture Group to retain the numbers
  const parts = word.split(/(\d+)/);
  const residualParts = parts.map((p) => capitalize(p));
  return residualParts.join("");
}

// A word like LEPCReports, or CNReports
function handleMixedCaseWord(word: string) {
  if (!word.match(/[A-Z][A-Z]+[a-z]/)) {
    return word;
  }
  // Find the last uppercase letter in the sequence of uppercase letters
  const match = word.match(/[A-Z]{2,}(?=[A-Z][a-z]|$)/);
  if (!match) {
    return word;
  }
  const acronym = match[0];
  const startIndex = match.index || 0;
  const endIndex = startIndex + acronym.length;
  // Split into [before acronym, acronym, after acronym]
  const parts = [word.slice(0, startIndex), acronym, word.slice(endIndex)];
  // Return the parts joined with spaces, keeping acronym in uppercase
  return parts
    .filter(Boolean) // Remove empty strings
    .map((part, index) => (index === 0 ? part : capitalize(part)))
    .join(" ");
}
